import {
  MIXPANEL_API_HOST,
  MIXPANEL_DEBUG,
  MIXPANEL_TOKEN,
  WEBAPP_MIXPANEL_TOKEN,
} from 'config';
import mixpanel from 'mixpanel-browser';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  TrackChangeClip,
  TrackEpisodeListen,
  TrackEpisodePageLoad,
  TrackPlayback,
  TrackPlaying,
  TrackPodcastPageLoad,
  TrackSeek,
  TrackSharePodcast,
  TrackShareEpisode,
  TrackToggleClipMute,
  TrackUserLogin,
  TrackUserSignup,
  TrackPromotePodcast,
  TrackFollowPodcast,
  TrackUnfollowPodcast,
  TrackClaimPodcastRequest,
  TrackClaimPodcastRequestGranted,
  TrackOwnershipGrantedCta,
  TrackDisplayFollowPrompt,
  TrackVisitPodcatcher,
  TrackSearchBarExpanded,
  TrackSearchResultSelected,
  SearchResultSelectedOpts,
  TrackSearchTrendingSelected,
  SearchTrendingSelectedOpts,
  TrackHomeFeedStartDiscovering,
  HomeFeedDiscoveryAction,
  TrackHomePageLoaded,
  TrackEpisodeComplete,
  TrackClickBlockedEpisodeCard,
  TrackEpisodePageBlockedStateCTA,
  TrackClickEpisodeCard,
  ClickEpisodeCardProps,
  TrackPersistentPlayerLinkClick,
  TrackClickSponsoredResultsModalCTA,
  EventTrackingContextType,
  TrackViewPodcastAnalytics,
  TrackTldlEmptyStateButtons,
  TrackTldlSummarizeEpisodes,
  TrackTldlAudioReady,
  TrackPlayTldlAudio,
  TrackTldlVisitFullEpisode,
  TrackTldlShareButton,
  TrackTldlSummarizeMore,
  TrackTldlBannerClick,
} from '../types';
import { EVENTS } from './constants';
import {
  getSound,
  register,
  track,
  setUserTrackingInfo,
  clearUserTrackingInfo,
  trackExperiments,
  trackMake,
  getPlaybackPropsFromSource,
  apiTrack,
} from './utils';

if (!MIXPANEL_TOKEN) {
  throw new Error('missing mixpanel token');
}

mixpanel.init(MIXPANEL_TOKEN, {
  api_host: MIXPANEL_API_HOST,
  batch_requests: false,
  debug: MIXPANEL_DEBUG,
});

if (!WEBAPP_MIXPANEL_TOKEN) {
  throw new Error('missing mixpanel token');
}

mixpanel.init(
  WEBAPP_MIXPANEL_TOKEN,
  {
    api_host: MIXPANEL_API_HOST,
    batch_requests: false,
    debug: MIXPANEL_DEBUG,
  },
  'make',
);

export default function useMixpanelEventTracking(): Omit<
  EventTrackingContextType,
  'trackSponsoredEpisodeClickThrough' | 'trackSponsoredEpisodePlayed'
> {
  const episodePageLoadTracked = useRef(false);
  const podcastPageLoadTracked = useRef(false);
  const homePageLoadTracked = useRef(false);
  const lastPlayedTldlJobCuid = useRef<string>();
  const location = useLocation();

  useEffect(() => {
    // any page tracking event can only fire once per location.  when location changes,
    // reset this ref variable so that the new page load can be tracked
    episodePageLoadTracked.current = false;
    podcastPageLoadTracked.current = false;
    homePageLoadTracked.current = false;
  }, [location]);

  useEffect(() => {
    register({ userAgent: navigator.userAgent });
  }, []);

  const trackEpisodePageLoad: TrackEpisodePageLoad = useCallback(
    (properties) => {
      if (episodePageLoadTracked.current) {
        return;
      }

      episodePageLoadTracked.current = true;

      apiTrack(EVENTS.episodePageLoaded, properties);
    },
    [],
  );

  const trackPodcastPageLoad: TrackPodcastPageLoad = useCallback(
    (clipsAdded) => {
      if (podcastPageLoadTracked.current) {
        return;
      }

      podcastPageLoadTracked.current = true;

      track(EVENTS.podcastPageLoaded, {
        clipsAdded,
      });
    },
    [],
  );

  const trackPlayback: TrackPlayback = useCallback((state, opts) => {
    const props = getPlaybackPropsFromSource(opts);

    if (state === 'play') {
      apiTrack(EVENTS.playEpisode, props);
    } else {
      track(EVENTS.pauseEpisode, props);
    }
  }, []);

  const trackSeek: TrackSeek = useCallback((seekType) => {
    track(EVENTS.skip, {
      buttonClick: seekType,
    });
  }, []);

  const trackPlaying: TrackPlaying = useCallback((podcastName, episodeName) => {
    track(EVENTS.playingEpisode, {
      eventGenerationSource: 'system',
      podcastName,
      episodeName,
    });
  }, []);

  const trackToggleClipMute: TrackToggleClipMute = useCallback(
    (volume, clip, source) => {
      track(EVENTS.toggleClipSound, {
        sound: getSound(volume),
        clipId: clip.clipId,
        episode: clip?.episode.title,
        podcast: clip?.podcast.title,
        source,
      });
    },
    [],
  );

  const trackChangeClip: TrackChangeClip = useCallback((direction, source) => {
    track(EVENTS.changeClip, {
      direction,
      source,
    });
  }, []);

  const trackShareEpisode: TrackShareEpisode = useCallback(
    () => track(EVENTS.shareEpisode),
    [],
  );

  const trackSharePodcast: TrackSharePodcast = useCallback(
    () => track(EVENTS.sharePodcast),
    [],
  );

  const trackEpisodeListen: TrackEpisodeListen = useCallback(
    (podcastName, episodeName) =>
      track(EVENTS.episodeListen, { podcastName, episodeName }),
    [],
  );

  const trackUserSignup: TrackUserSignup = useCallback((email) => {
    track(EVENTS.signUp);
    mixpanel.alias(email);
  }, []);

  const trackUserLogin: TrackUserLogin = useCallback(
    () => track(EVENTS.login),
    [],
  );

  const trackPromotePodcast: TrackPromotePodcast = useCallback(
    () => track(EVENTS.promotePodcast),
    [],
  );

  const trackFollowPodcast: TrackFollowPodcast = useCallback(
    (podcastName, source) =>
      track(EVENTS.followPodcast, {
        podcast: podcastName,
        source,
      }),
    [],
  );

  const trackUnfollowPodcast: TrackUnfollowPodcast = useCallback(
    (podcastName, source) =>
      track(EVENTS.unfollowPodcast, {
        podcast: podcastName,
        source,
      }),
    [],
  );

  const trackClaimPodcastRequest: TrackClaimPodcastRequest = useCallback(
    () => trackMake(EVENTS.claimPodcastEmailCTA),
    [],
  );

  const trackClaimPodcastRequestGranted: TrackClaimPodcastRequestGranted =
    useCallback(() => trackMake(EVENTS.ownershipRequestEmailCTA), []);

  const trackOwnershipGrantedCta: TrackOwnershipGrantedCta = useCallback(
    () => trackMake(EVENTS.ownershipGrantedEmailCTA),
    [],
  );

  const trackDisplayFollowPrompt: TrackDisplayFollowPrompt = useCallback(
    () => track(EVENTS.displayFollowPrompt),
    [],
  );

  const trackVisitPodcatcher: TrackVisitPodcatcher = useCallback(
    (buttonClick) => track(EVENTS.visitPodcatcher, { buttonClick }),
    [],
  );

  const trackSearchBarExpanded: TrackSearchBarExpanded = useCallback(
    () => track(EVENTS.searchBarExpanded),
    [],
  );

  const trackSearchResultSelected: TrackSearchResultSelected = useCallback(
    (opts: SearchResultSelectedOpts) =>
      track(EVENTS.searchResultSelected, opts),
    [],
  );

  const trackSearchTrendingSelected: TrackSearchTrendingSelected = useCallback(
    (opts: SearchTrendingSelectedOpts) =>
      track(EVENTS.searchTrendingSelected, opts),
    [],
  );

  const trackHomePageLoaded: TrackHomePageLoaded = useCallback(() => {
    if (homePageLoadTracked.current) {
      return;
    }
    homePageLoadTracked.current = true;

    track(EVENTS.homePageLoaded);
  }, []);

  const trackHomeFeedStartDiscovering: TrackHomeFeedStartDiscovering =
    useCallback(
      (source: HomeFeedDiscoveryAction) =>
        track(EVENTS.homeFeedStartDiscovering, { source }),
      [],
    );

  const trackEpisodeComplete: TrackEpisodeComplete = useCallback(
    (podcastName, episodeName) =>
      track(EVENTS.episodeComplete, { podcastName, episodeName }),
    [],
  );

  const trackClickBlockedEpisodeCard: TrackClickBlockedEpisodeCard =
    useCallback(
      (podcastName, episodeName) =>
        track(EVENTS.clickBlockedEpisodeCard, { podcastName, episodeName }),
      [],
    );

  const trackEpisodePageBlockedStateCTA: TrackEpisodePageBlockedStateCTA =
    useCallback(() => track(EVENTS.episodePageBlockedStateCTA), []);

  const trackClickEpisodeCard: TrackClickEpisodeCard = useCallback(
    (props: ClickEpisodeCardProps) => track(EVENTS.clickEpisodeCard, props),
    [],
  );
  const trackPersistentPlayerLinkClick: TrackPersistentPlayerLinkClick =
    useCallback(
      (buttonClick) => track(EVENTS.persistentPlayerLink, { buttonClick }),
      [],
    );

  const trackClickSponsoredResultsModalCTA: TrackClickSponsoredResultsModalCTA =
    useCallback(() => track(EVENTS.sponsoredResultsModalCTA), []);

  const trackViewPodcastAnalytics: TrackViewPodcastAnalytics = useCallback(
    () => track(EVENTS.viewPodcastAnalytics),
    [],
  );

  const trackTldlEmptyStateButtons: TrackTldlEmptyStateButtons = useCallback(
    (buttonClick) => track(EVENTS.tldlEmptyStateButtons, { buttonClick }),
    [],
  );

  const trackTldlSummarizeEpisodes: TrackTldlSummarizeEpisodes = useCallback(
    ({ episodeCount, targetDurationMins }) =>
      track(EVENTS.tldlSummarizeEpisodes, {
        numberOfEpisodes: episodeCount,
        targetDuration: targetDurationMins,
      }),
    [],
  );

  const trackTldlAudioReady: TrackTldlAudioReady = useCallback(
    () => track(EVENTS.tldlAudioReady),
    [],
  );

  const trackPlayTldlAudio: TrackPlayTldlAudio = useCallback((cuid) => {
    if (lastPlayedTldlJobCuid.current !== cuid) {
      track(EVENTS.playTldlAudio);
      lastPlayedTldlJobCuid.current = cuid;
    }
  }, []);

  const trackTldlVisitFullEpisode: TrackTldlVisitFullEpisode = useCallback(
    () => track(EVENTS.tldlVisitFullEpisode),
    [],
  );

  const trackTldlShareButton: TrackTldlShareButton = useCallback(
    () => track(EVENTS.tldlShareButton),
    [],
  );

  const trackTldlSummarizeMore: TrackTldlSummarizeMore = useCallback(
    () => track(EVENTS.tldlSummarizeMore),
    [],
  );

  const trackTldlBannerClick: TrackTldlBannerClick = useCallback(
    () => track(EVENTS.clickTldlBanner),
    [],
  );

  return useMemo(
    () => ({
      trackChangeClip,
      trackEpisodeListen,
      trackEpisodePageLoad,
      trackExperiments,
      setUserTrackingInfo,
      clearUserTrackingInfo,
      trackPlayback,
      trackPlaying,
      trackPodcastPageLoad,
      trackSeek,
      trackShareEpisode,
      trackSharePodcast,
      trackToggleClipMute,
      trackUserSignup,
      trackUserLogin,
      trackPromotePodcast,
      trackFollowPodcast,
      trackUnfollowPodcast,
      trackClaimPodcastRequest,
      trackClaimPodcastRequestGranted,
      trackOwnershipGrantedCta,
      trackDisplayFollowPrompt,
      trackVisitPodcatcher,
      trackSearchBarExpanded,
      trackSearchResultSelected,
      trackSearchTrendingSelected,
      trackHomePageLoaded,
      trackHomeFeedStartDiscovering,
      trackEpisodeComplete,
      trackClickBlockedEpisodeCard,
      trackEpisodePageBlockedStateCTA,
      trackClickEpisodeCard,
      trackPersistentPlayerLinkClick,
      trackClickSponsoredResultsModalCTA,
      trackViewPodcastAnalytics,
      trackTldlEmptyStateButtons,
      trackTldlSummarizeEpisodes,
      trackTldlAudioReady,
      trackPlayTldlAudio,
      trackTldlVisitFullEpisode,
      trackTldlShareButton,
      trackTldlSummarizeMore,
      trackTldlBannerClick,
    }),
    [
      trackChangeClip,
      trackEpisodeListen,
      trackEpisodePageLoad,
      trackPlayback,
      trackPlaying,
      trackPodcastPageLoad,
      trackSeek,
      trackShareEpisode,
      trackSharePodcast,
      trackToggleClipMute,
      trackUserSignup,
      trackUserLogin,
      trackPromotePodcast,
      trackFollowPodcast,
      trackUnfollowPodcast,
      trackClaimPodcastRequest,
      trackClaimPodcastRequestGranted,
      trackOwnershipGrantedCta,
      trackDisplayFollowPrompt,
      trackVisitPodcatcher,
      trackSearchBarExpanded,
      trackSearchResultSelected,
      trackSearchTrendingSelected,
      trackHomePageLoaded,
      trackHomeFeedStartDiscovering,
      trackEpisodeComplete,
      trackClickBlockedEpisodeCard,
      trackEpisodePageBlockedStateCTA,
      trackClickEpisodeCard,
      trackPersistentPlayerLinkClick,
      trackClickSponsoredResultsModalCTA,
      trackViewPodcastAnalytics,
      trackTldlEmptyStateButtons,
      trackTldlSummarizeEpisodes,
      trackTldlAudioReady,
      trackPlayTldlAudio,
      trackTldlVisitFullEpisode,
      trackTldlShareButton,
      trackTldlSummarizeMore,
      trackTldlBannerClick,
    ],
  );
}
